import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CheckCircle2, Pencil, Printer, Trash2 } from 'lucide-react';
import { STATUS_MAP, PRIORITY_MAP } from '@/lib/status-colors';

/**
 * What the order is, in the words the floor and the customer both use.
 *
 * The heading was the customer and their firm, which the facts band directly
 * below already gives two columns to — so the largest text on the page said
 * nothing the rest of it did not, and never said what was being made. The
 * product leads now; a multi-item order names the first and counts the rest.
 */
function orderTitle(order) {
  const items = order.line_items?.filter(item => !item.is_split_child) || [];
  const named = items.find(item => item.item_name || item.name);
  if (!named) return order.customer_name || order.order_id;

  const title = named.item_name || named.name;
  return items.length > 1 ? `${title} +${items.length - 1} more` : title;
}

export default function OrderHeader({ order, isAdmin, canEdit, canPrint, canComplete, onBack, onEditOrder, onDeleteOrder, onCompleteOrder, onPrintJobCard }) {
  const who = [order.customer_name, order.firm_name].filter(Boolean).join(' — ');

  return (
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-xl font-bold text-slate-900" data-testid="order-title">
              {orderTitle(order)}
            </h1>
            {order.is_internal_order && (
              <Badge className="text-xs bg-amber-50 text-amber-700 border-amber-200">Internal Stock Order</Badge>
            )}
            <Badge className={`text-xs ${STATUS_MAP[order.status]?.class}`}>
              {STATUS_MAP[order.status]?.label}
            </Badge>
            <Badge className={`text-xs ${PRIORITY_MAP[order.priority]?.class}`}>
              {PRIORITY_MAP[order.priority]?.label}
            </Badge>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            {who ? `${who} | ` : ''}{order.order_id} | Voice: {order.voice_number} | {order.job_type?.toUpperCase()}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onEditOrder} data-testid="edit-order-btn"
            className="border-slate-300 text-slate-700 hover:bg-slate-50">
            <Pencil className="h-4 w-4 mr-1" /> Edit Order
          </Button>
        )}
        {canComplete && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCompleteOrder}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="complete-order-btn"
          >
            <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Delivered
          </Button>
        )}
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={onDeleteOrder}
            className="border-red-200 text-red-600 hover:bg-red-50"
            data-testid="delete-order-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete Order
          </Button>
        )}
        {canPrint && (
          <Button variant="outline" size="sm" onClick={onPrintJobCard} data-testid="print-job-card-btn"
            className="border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white">
            <Printer className="h-4 w-4 mr-1" /> Print Job Cards
          </Button>
        )}
      </div>
    </div>
  );
}
